/**
 * The chat bubble — the tinted, rounded container a message body sits in, the boxes that
 * hold it, and the in-flow footer the reaction row rides.
 *
 * Colors are a `variant` choice, not a hard-coded fill: every variant resolves to the
 * project's own `bg-*` / `text-*` / `border-*` tokens, so the bubble themes with the rest
 * of the app (including dark mode) for free. A caller that needs something the variants
 * do not cover passes `className` — `cn` places it last, so it wins the conflict.
 */

import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "cn";
import type * as React from "react";

function BubbleGroup({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="bubble-group"
      className={cn("flex min-w-0 flex-col gap-1", className)}
      {...props}
    />
  );
}

/**
 * The column a message's bubble and its chrome sit in — Figma's `VStack`.
 *
 * Its width is the message's BUDGET — the bubble's maximum width — and it does not hug its
 * contents, which is what lets a bubble that is allowed to grow actually grow.
 *
 * It is NOT the hover `group` (that is `BubbleBox`, the bubble's own box) and it is not the
 * reaction row's budget (that is `BubbleCluster`). A group here revealed the action bar
 * whenever the pointer was anywhere on the budget's line, including the empty space beside
 * a one-word bubble.
 *
 * Which edge the bubble sits on is the caller's `items-*` — this box fills the width either
 * way, so `BubbleGroup`'s own alignment cannot do it.
 */
function BubbleColumn({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="bubble-column"
      className={cn(
        /* sorted */
        "flex",
        "flex-col",
        "w-full max-w-[min(32rem,100%)] min-w-0",
        className,
      )}
      {...props}
    />
  );
}

/**
 * The bubble's box and its reaction row, as one shrink-to-fit column — and the reaction
 * row's BUDGET.
 *
 * It hugs the bubble, so the row stretched across it is the bubble's width and expands and
 * shrinks with it. Two things keep that honest:
 *
 * - `BubbleFooter`'s `contain: inline-size` — the row's CONTENTS contribute nothing to this
 *   box's shrink-to-fit width, so the width the row measures itself against is a function
 *   of the bubble and never of the row's own last answer. Without it this box would widen
 *   to hold every pill on one line.
 * - A caller's `min-w-*` FLOOR — a two-character message is ~90px wide, which fits one pill
 *   and nothing else, so the row that message's reactions need is wider than the bubble
 *   they hang off. `REACTION_ROW_MIN_WIDTH` (`channel-message-reaction-footer`) is that
 *   floor, worn here and only while the row is drawn. A min-width is not deaf to
 *   containment and is not meant to be: it is what widens this box past the bubble.
 *
 * The bubble and the action bar live one level further in, in `BubbleBox`, precisely so
 * that floor cannot reach them: the bar anchors to the BUBBLE's edge, and a bar pinned to
 * this box's right edge would float 190px clear of a one-word bubble, attached to nothing.
 * Which side the box sits on when the floor is wider than it is the caller's `self-*` on
 * the box — never `items-*` here, which would take the row's stretch away with it.
 */
function BubbleCluster({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="bubble-cluster"
      className={cn(
        /* sorted */
        "flex",
        "flex-col",
        "w-fit max-w-full min-w-0",
        className,
      )}
      {...props}
    />
  );
}

/**
 * The bubble and the chrome that floats over it.
 *
 * Shrink-to-fit, so the bubble hugs a short message and the action bar anchored inside this
 * box lands on the BUBBLE's edge rather than on the column's. Anchored to the column, a
 * ~150px bar would sit far out to the right of a one-word incoming bubble, attached to
 * nothing.
 *
 * It is the hover `group`. The chrome a hover reveals answers to the pointer being on the
 * MESSAGE, which is this box and not the budget around it — `BubbleColumn` is the full
 * width of the column, so a group there revealed the action bar whenever the pointer was
 * anywhere on the message's line.
 */
function BubbleBox({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="bubble-box"
      className={cn(
        /* sorted */
        "relative flex",
        "flex-col",
        "w-fit max-w-full min-w-0",
        "group",
        className,
      )}
      {...props}
    />
  );
}

const bubbleVariants = cva(
  cn(
    /* sorted */
    "relative flex",
    "flex-col gap-1",
    // `w-fit` lets a bubble hug a short message, but a shrink-to-fit box does not
    // constrain a fixed-width child: without `max-w-full` anything wider than the column
    // — a long unbroken word, a fixed-width card — overflows it, and the child's own
    // `max-w-full` cannot save it: that resolves against this box, which the child is
    // what sized.
    "w-fit max-w-full min-w-0",
    "p-2",
    "rounded-2xl",
    "group/bubble",
  ),
  {
    variants: {
      variant: {
        /** Own / outgoing — Figma `bg-primary-min`. */
        default: "bg-bg-primary-min text-text-dark-max",
        secondary: "bg-bg-container text-text-dark-max",
        /** Incoming — Figma `bg-dark-min`. */
        muted: "bg-bg-dark-min text-text-dark-max",
        tinted: "bg-bg-surface-elevate-low text-text-dark-max",
        outline:
          "bg-bg-transparent text-text-dark-max border border-border-dark-low",
        ghost: "bg-bg-transparent text-text-dark-max",
        destructive: "bg-bg-negative-min text-text-dark-max",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

function Bubble({
  variant = "default",
  align = "start",
  className,
  ...props
}: React.ComponentProps<"div"> &
  VariantProps<typeof bubbleVariants> & {
    align?: "start" | "end";
  }) {
  return (
    <div
      data-slot="bubble"
      data-variant={variant}
      data-align={align}
      className={cn(bubbleVariants({ variant }), className)}
      {...props}
    />
  );
}

function BubbleContent({
  className,
  render,
  children,
  ...props
}: React.ComponentProps<"div"> & {
  render?: (
    props: React.HTMLAttributes<HTMLElement> & { "data-slot": string },
  ) => React.ReactNode;
}) {
  const contentClassName = cn(
    /* sorted */
    "flex overflow-hidden",
    "flex-col gap-1",
    "w-fit max-w-full min-w-0",
    "wrap-break-word [button]:text-left [button,a]:transition-colors",
    className,
  );

  if (render) {
    return render({
      ...props,
      "data-slot": "bubble-content",
      className: contentClassName,
      children,
    });
  }

  return (
    <div {...props} data-slot="bubble-content" className={contentClassName}>
      {children}
    </div>
  );
}

/**
 * The reaction row's slot — an in-flow sibling of `BubbleBox` inside `BubbleCluster`, never
 * an overlay.
 *
 * Its ORDER never mirrors. An own message's reactions read in the same order as an incoming
 * one's — pills, then the expander, then the add button, left to right. The row used to
 * reverse itself for `align === "end"`, which put an own message's pills on the right and
 * its controls on the left: two layouts to learn instead of one. Which edge the row packs
 * against still follows the bubble, and that is the caller's `justify-*`.
 */
function BubbleFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="bubble-footer"
      className={cn(
        /* sorted */
        // IN FLOW, and that is the whole point: the reaction row used to be an absolutely
        // positioned overlay, which could hold exactly one line and covered the next
        // message the moment it held two. Here its height is the layout's, so expanding it
        // pushes everything below it down.
        //
        // `relative z-10` is not decoration: `Bubble` is `relative`, and a positioned
        // element paints above a static sibling whatever the source order says — without
        // it the 4px overlap band would render UNDER the bubble and the pills would read
        // as clipped.
        "relative z-10 flex",
        "items-start gap-1",
        // Exactly as wide as `BubbleCluster`, which hugs the bubble (down to the cluster's
        // own floor) — so the row expands and shrinks with the bubble instead of spanning
        // the whole budget. Stretch (the cluster's default `align-items`) hands it that
        // width; `contain: inline-size` is what makes taking it safe, by computing this
        // box's width as if it had no contents. A row that counted toward its parent's
        // shrink-to-fit width would widen the very box it measures itself against.
        "w-auto min-w-0",
        "[contain:inline-size]",
        // Figma insets the footer 8px from the bubble's edge and overlaps that edge. The
        // overlap is this margin minus whatever gap the cluster spends between the two —
        // `BubbleCluster` spends none, so the two numbers are tied: give it a gap and this
        // one has to move with it.
        "px-2 -mt-2",
        className,
      )}
      {...props}
    />
  );
}

export {
  Bubble,
  BubbleBox,
  BubbleCluster,
  BubbleColumn,
  BubbleContent,
  BubbleFooter,
  BubbleGroup,
  bubbleVariants,
};
